// De praktijkkant van een inzicht in de redactie: randvoorwaarden, valkuilen en
// de doorlooptijd. Dit is wat een redacteur uit een gemodereerde praktijkbijdrage
// overneemt; de publieke API serveert het daarna als `practice` / `leadTime`.
//
// SPIEGEL van functions/intelligence/insightPractice.js — wijzig BEIDE. De
// sleutels moeten gelijk blijven: het formulier op de marketingsite stuurt een
// band, de server slaat hem op, en deze lijst bepaalt wat een redacteur kan
// kiezen. Een sleutel die hier bijkomt en daar niet, wordt bij het opslaan stil
// geweigerd (`normaliseLeadTime` geeft dan ''). De spiegel is gepind door
// insightPractice.parity.test.js, dat het echte bestand uit functions/ leest —
// een comment alleen houdt twee lijsten niet gelijk.
// AI-CONTEXT: See docs/pitfalls.md#dev-mirrored-constant-guarded-only-by-a-comment
// AI-CONTEXT: See docs/features/inzicht-praktijkbijdrage.md

/**
 * @typedef {object} PracticeRow
 * @property {'randvoorwaarde'|'valkuil'} kind - Wat voor regel dit is
 * @property {string} text - De gemodereerde regel zelf
 * @property {string} source - De herkomst zoals de bijdrager hem toestond; leeg = geen bron tonen
 */

/** De twee soorten praktijkregels, met het label dat de redacteur ziet. */
export const PRACTICE_KINDS = [
    { key: 'randvoorwaarde', label: 'Randvoorwaarde' },
    { key: 'valkuil', label: 'Valkuil' },
]

/**
 * De doorlooptijd-banden van het bijdrageformulier. '' is 'niet ingevuld' en
 * een echte keuze: de publieke API laat de zin dan weg.
 */
export const LEAD_TIME_OPTIONS = [
    { key: '', label: 'Niet ingevuld' },
    { key: 'weken', label: 'Binnen een paar weken' },
    { key: '1-3-maanden', label: 'Na 1–3 maanden' },
    { key: '3-6-maanden', label: 'Na 3–6 maanden' },
    { key: 'langer', label: 'Pas na meer dan een half jaar' },
    { key: 'onbekend', label: 'Onbekend (bijdragers wisten het niet)' },
]

/** @param {*} v @returns {string} */
const str = (v) => (typeof v === 'string' ? v : '')

/**
 * De praktijkregels van een opgeslagen inzicht als bewerkbare rijen. Altijd een
 * array, nooit undefined — een ontbrekend veld is een leeg blok, geen fout.
 *
 * @param {{Practice?: Array<object>}} insight - Het QuestionInsights-document
 * @returns {Array<PracticeRow>}
 */
export const practiceRowsOf = (insight) => (Array.isArray(insight && insight.Practice) ? insight.Practice : [])
    .filter((row) => row && typeof row === 'object')
    .map((row) => ({
        kind: PRACTICE_KINDS.some((k) => k.key === row.kind) ? row.kind : 'randvoorwaarde',
        text: str(row.text),
        source: str(row.source),
    }))

/** @returns {PracticeRow} Een lege rij om in te typen */
export const emptyPracticeRow = () => ({ kind: 'randvoorwaarde', text: '', source: '' })

/**
 * De opgeslagen doorlooptijd van een inzicht; '' wanneer er niets staat of de
 * band niet in de lijst voorkomt.
 *
 * @param {{LeadTime?: string}} insight
 * @returns {string}
 */
export const leadTimeOf = (insight) => {
    const waarde = str(insight && insight.LeadTime)
    return LEAD_TIME_OPTIONS.some((o) => o.key === waarde) ? waarde : ''
}

/**
 * Of twee lijsten praktijkregels hetzelfde zeggen. Gebruikt om te zien of de
 * redacteur iets veranderd heeft dat een binnenkomende snapshot niet mag
 * overschrijven.
 * AI-CONTEXT: See docs/pitfalls.md#state-draft-wiped-by-firestore-snapshot
 *
 * @param {Array<PracticeRow>} a
 * @param {Array<PracticeRow>} b
 * @returns {boolean}
 */
export const samePractice = (a, b) => {
    const links = Array.isArray(a) ? a : []
    const rechts = Array.isArray(b) ? b : []
    if (links.length !== rechts.length) return false
    return links.every((row, i) => row.kind === rechts[i].kind
        && row.text === rechts[i].text
        && row.source === rechts[i].source)
}
